class SidefillSmartBundle extends HTMLElement {
  constructor() {
    super();
    this.form = null;
    this.onChange = this.onChange.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onQuantityChange = this.onQuantityChange.bind(this);
  }

  connectedCallback() {
    this.form = document.getElementById(this.dataset.formId);
    if (!this.form || this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';
    this.variants = this.readJson('[data-main-variants]') || [];
    this.addEventListener('change', this.onChange);
    this.querySelector('[data-select-all]')?.addEventListener('click', () => this.selectAll());
    this.onUpsellClick = (event) => {
      const addButton = event.target.closest('[data-upsell-add]');
      if (addButton) {
        this.addUpsellItem(addButton);
        return;
      }
      if (event.target.closest('[data-upsell-continue], [data-upsell-continue-backdrop]')) this.closeUpsell();
    };
    this.addEventListener('click', this.onUpsellClick);
    this.form.addEventListener('submit', this.onSubmit, true);
    this.quantityInput = this.form.querySelector('[name="quantity"]')
      || document.querySelector(`[name="quantity"][form="${CSS.escape(this.dataset.formId)}"]`);
    this.quantitySelector = this.quantityInput?.closest('quantity-selector');
    this.quantityInput?.addEventListener('input', this.onQuantityChange);
    this.quantityInput?.addEventListener('change', this.onQuantityChange);
    this.quantitySelector?.addEventListener('click', this.onQuantityChange);
    this.restoreState();
    this.render();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
    this.removeEventListener('click', this.onUpsellClick);
    if (this.onUpsellKeydown) document.removeEventListener('keydown', this.onUpsellKeydown);
    this.form?.removeEventListener('submit', this.onSubmit, true);
    this.quantityInput?.removeEventListener('input', this.onQuantityChange);
    this.quantityInput?.removeEventListener('change', this.onQuantityChange);
    this.quantitySelector?.removeEventListener('click', this.onQuantityChange);
  }

  readJson(selector) {
    try {
      return JSON.parse(this.querySelector(selector)?.textContent || 'null');
    } catch (_) {
      return null;
    }
  }

  onChange(event) {
    if (event.target.matches('[data-upsell-sleeve-variants] input')) {
      const image = this.querySelector('.sf-upsell__image--sleeve');
      if (image && event.target.dataset.upsellImageUrl) image.src = event.target.dataset.upsellImageUrl;
      return;
    }
    if (event.target.matches('[data-addon-toggle], [data-sleeve-variants] input')) {
      this.saveState();
      this.render();
    }
  }

  onQuantityChange() {
    requestAnimationFrame(() => this.render());
  }

  selectAll() {
    this.querySelectorAll('[data-addon-toggle]').forEach((input) => { input.checked = true; });
    this.saveState();
    this.render();
  }

  get stateStore() {
    window.sidefillBundleSelections ||= {};
    return window.sidefillBundleSelections;
  }

  get stateKey() {
    return this.dataset.productId || this.dataset.formId;
  }

  saveState() {
    this.stateStore[this.stateKey] = {
      sleeve: this.selected('sleeve'),
      beads: this.selected('beads'),
      sleeveVariantId: this.getSleeveInput()?.value || null
    };
  }

  restoreState() {
    const state = this.stateStore[this.stateKey];
    if (!state) return;

    ['sleeve', 'beads'].forEach((type) => {
      const toggle = this.querySelector(`[data-addon-toggle="${type}"]`);
      if (toggle) toggle.checked = Boolean(state[type]);
    });

    if (state.sleeveVariantId) {
      const sleeveVariant = this.querySelector(`[data-sleeve-variants] input[value="${CSS.escape(String(state.sleeveVariantId))}"]`);
      if (sleeveVariant) sleeveVariant.checked = true;
    }
  }

  selected(type) {
    return Boolean(this.querySelector(`[data-addon-toggle="${type}"]`)?.checked);
  }

  getMainVariant() {
    const id = Number(new FormData(this.form).get('id'));
    return this.variants.find((variant) => Number(variant.id) === id) || this.variants[0] || null;
  }

  getMainQuantity() {
    return Math.max(1, Number(new FormData(this.form).get('quantity') || 1));
  }

  getSleeveInput() {
    return this.querySelector('[data-sleeve-variants] input:checked, [data-sleeve-variant]');
  }

  formatMoney(cents) {
    return `${(Number(cents || 0) / 100).toFixed(2).replace('.', ',')} CHF`;
  }

  render() {
    ['sleeve', 'beads'].forEach((type) => {
      const active = this.selected(type);
      this.querySelector(`[data-addon-card="${type}"]`)?.classList.toggle('is-selected', active);
      if (type === 'sleeve') {
        const fieldset = this.querySelector('[data-sleeve-variants]');
        if (fieldset) fieldset.disabled = !active;
      }
    });

    const sleeveInput = this.getSleeveInput();
    const sleeveImage = this.querySelector('[data-addon-card="sleeve"] .sf-smart-bundle__image');
    if (sleeveImage && sleeveInput?.dataset.addonImageUrl) sleeveImage.src = sleeveInput.dataset.addonImageUrl;

    const mainVariant = this.getMainVariant();
    const mainQuantity = this.getMainQuantity();
    const mainOriginalPrice = Number(mainVariant?.price || 0) * mainQuantity;
    const quantityDiscountThreshold = Number(this.dataset.quantityDiscountThreshold || 0);
    const quantityDiscountPercent = Number(this.dataset.quantityDiscountPercent || 0);
    const quantityDiscountActive = quantityDiscountPercent > 0 && mainQuantity >= quantityDiscountThreshold;
    const mainPrice = quantityDiscountActive
      ? Math.round(mainOriginalPrice * (100 - quantityDiscountPercent) / 100)
      : mainOriginalPrice;
    const quantitySaving = mainOriginalPrice - mainPrice;
    const sleevePrice = this.selected('sleeve') ? Number(sleeveInput?.dataset.addonPrice || 0) : 0;
    const beadsInput = this.querySelector('[data-addon-toggle="beads"]');
    const beadsPrice = this.selected('beads') ? Number(beadsInput?.dataset.addonPrice || 0) : 0;
    const total = mainPrice + sleevePrice + beadsPrice;
    const sleeveSaving = this.selected('sleeve') ? Number(this.dataset.sleeveSaving || 0) : 0;
    const shippingSaving = total >= Number(this.dataset.freeShippingThreshold || 0)
      ? Number(this.dataset.shippingSaving || 0)
      : 0;
    const saving = quantitySaving + sleeveSaving + shippingSaving;
    const selections = Number(this.selected('sleeve')) + Number(this.selected('beads'));
    const status = this.querySelector('[data-bundle-status-text]');
    const totalNode = this.querySelector('[data-bundle-total]');

    if (status) {
      if (selections === 0) status.textContent = 'Alles optional – du entscheidest.';
      else if (quantityDiscountActive && shippingSaving > 0) status.textContent = `${quantityDiscountPercent} % Mengenrabatt + kostenloser Versand · Du sparst ${this.formatMoney(saving)}`;
      else if (quantityDiscountActive) status.textContent = `${quantityDiscountPercent} % Mengenrabatt aktiv · Du sparst ${this.formatMoney(saving)}`;
      else if (shippingSaving > 0) status.textContent = `Kostenloser Versand freigeschaltet · Du sparst ${this.formatMoney(saving)}`;
      else if (saving > 0) status.textContent = `Bundle-Rabatt aktiv · Du sparst ${this.formatMoney(saving)}`;
      else status.textContent = 'Wird gemeinsam mit deiner SideFill hinzugefügt.';
    }
    if (totalNode) totalNode.textContent = selections > 0 ? `Zusammen ${this.formatMoney(total)}` : '';
    this.querySelector('[data-select-all]')?.classList.toggle('is-complete', selections === 2);
  }

  getMixItems() {
    // Multi-color sets built in the sidefill-volume-tiers "Farben mischen" panel replace the single main line.
    const volumeTiers = document.querySelector(`sidefill-volume-tiers[data-form-id="${CSS.escape(this.dataset.formId)}"]`);
    return volumeTiers?.getMixItems?.() || null;
  }

  getItems() {
    const formData = new FormData(this.form);
    const mainId = Number(formData.get('id'));
    if (!mainId) return [];
    const mixItems = this.getMixItems();
    const mainItem = { id: mainId, quantity: this.getMainQuantity() };
    const properties = {};
    for (const [key, value] of formData.entries()) {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match && value !== '') properties[match[1]] = value;
    }
    if (Object.keys(properties).length) mainItem.properties = properties;

    const items = mixItems ? [...mixItems] : [mainItem];
    if (this.selected('sleeve')) {
      const sleeveId = Number(this.getSleeveInput()?.value || 0);
      if (sleeveId) items.push({ id: sleeveId, quantity: 1 });
    }
    if (this.selected('beads')) {
      const beadsId = Number(this.querySelector('[data-addon-toggle="beads"]')?.dataset.variantId || 0);
      if (beadsId) items.push({ id: beadsId, quantity: 1 });
    }
    return items;
  }

  async getFreshSections(sectionIds) {
    const root = String(Shopify.routes.root || '/');
    const sections = {};

    await Promise.all(sectionIds.map(async (sectionId) => {
      const url = `${root}?section_id=${encodeURIComponent(sectionId)}`;
      const response = await fetch(url, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        cache: 'no-store'
      });

      if (!response.ok) throw new Error('Der Warenkorb konnte nicht aktualisiert werden.');
      sections[sectionId] = await response.text();
    }));

    return sections;
  }

  upsellAvailable() {
    if (!this.querySelector('[data-upsell] [data-upsell-add]:not(:disabled)')) return false;
    try {
      return sessionStorage.getItem('sf-upsell-seen') !== '1';
    } catch (_) {
      return true;
    }
  }

  markUpsellSeen() {
    try {
      sessionStorage.setItem('sf-upsell-seen', '1');
    } catch (_) { /* Storage blockiert – Popup erscheint dann pro Pageview maximal einmal. */ }
  }

  async onSubmit(event) {
    const wantsUpsell = !this.selected('sleeve') && !this.selected('beads') && this.upsellAvailable();
    if (!this.selected('sleeve') && !this.selected('beads') && !this.getMixItems() && !wantsUpsell) return;
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    if (!this.form.checkValidity()) {
      this.form.reportValidity();
      return;
    }

    const submitButtons = Array.from(this.form.elements).filter((element) => element.type === 'submit');
    submitButtons.forEach((button) => {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    });
    this.querySelector('[data-bundle-error]').hidden = true;
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

    try {
      const payload = {
        items: this.getItems(),
        sections_url: window.location.pathname
      };
      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(payload)
      });
      const responseJson = await response.json();
      if (!response.ok) throw new Error(responseJson.description || 'Das Bundle konnte nicht hinzugefügt werden.');

      this.pendingAddedItems = responseJson.items || [];
      if (wantsUpsell) {
        this.showUpsell();
      } else {
        await this.finalizeCart();
      }
    } catch (error) {
      const errorNode = this.querySelector('[data-bundle-error]');
      if (errorNode) {
        errorNode.textContent = error.message;
        errorNode.hidden = false;
      }
      this.form.dispatchEvent(new CustomEvent('cart:error', { bubbles: true, detail: { error: error.message } }));
    } finally {
      submitButtons.forEach((button) => {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      });
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
    }
  }

  // Warenkorb-Abschluss nach jedem Add-Pfad: Drawer mit frisch berechneten
  // Rabatten öffnen (Shopify berechnet automatische Rabatte erst NACH dem
  // cart/add-Request, deshalb Cart + Sections nochmals holen).
  async finalizeCart() {
    if (window.themeVariables.settings.cartType === 'page' || window.themeVariables.settings.pageType === 'cart') {
      window.location.href = `${Shopify.routes.root}cart`;
      return;
    }
    const sections = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
    const [cart, freshSections] = await Promise.all([
      fetch(`${Shopify.routes.root}cart.js`, { cache: 'no-store' }).then((result) => result.json()),
      this.getFreshSections(sections)
    ]);
    cart.sections = freshSections;
    this.form.dispatchEvent(new CustomEvent('variant:add', {
      bubbles: true,
      detail: { items: this.pendingAddedItems || [], cart, onSuccessDo: 'force_open_drawer' }
    }));
    document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
      bubbles: true,
      detail: { baseEvent: 'variant:add', onSuccessDo: 'force_open_drawer', cart }
    }));
  }

  showUpsell() {
    const upsell = this.querySelector('[data-upsell]');
    if (!upsell) return this.finalizeCart();
    this.markUpsellSeen();
    upsell.hidden = false;
    this.onUpsellKeydown = (keyEvent) => {
      if (keyEvent.key === 'Escape') this.closeUpsell();
    };
    document.addEventListener('keydown', this.onUpsellKeydown);
    upsell.querySelector('[data-upsell-sheet]')?.focus({ preventScroll: true });
  }

  async closeUpsell() {
    const upsell = this.querySelector('[data-upsell]');
    if (upsell) upsell.hidden = true;
    document.removeEventListener('keydown', this.onUpsellKeydown);
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));
    try {
      await this.finalizeCart();
    } finally {
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
    }
  }

  async addUpsellItem(button) {
    let variantId = Number(button.dataset.variantId || 0);
    if (button.dataset.upsellAdd === 'sleeve') {
      const checked = this.querySelector('[data-upsell-sleeve-variants] input:checked');
      if (checked) variantId = Number(checked.value);
    }
    if (!variantId) return;
    button.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      });
      const responseJson = await response.json();
      if (!response.ok) throw new Error(responseJson.description || 'Konnte nicht hinzugefügt werden.');
      (this.pendingAddedItems ||= []).push(...(responseJson.items || []));
      button.disabled = true;
      button.textContent = '✓ Hinzugefügt';
      this.querySelector(`[data-upsell-item="${button.dataset.upsellAdd}"] [data-upsell-sleeve-variants]`)?.querySelectorAll('input').forEach((input) => { input.disabled = true; });
    } catch (_) {
      button.textContent = 'Nochmals versuchen';
    } finally {
      button.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('sidefill-smart-bundle')) {
  customElements.define('sidefill-smart-bundle', SidefillSmartBundle);
}
